import { useState, useEffect, useCallback } from "react";
import { Bell, BellRing, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { enablePush, pushStatus, pushSupported } from "@/lib/push";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pstate, setPstate] = useState("off");

  const load = useCallback(() => {
    api.get("/notifications").then((r) => { setItems(r.data.items || []); setUnread(r.data.unread || 0); }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    pushStatus().then(setPstate).catch(() => {});
    const t = setInterval(load, 60000);
    const onRefresh = () => load();
    window.addEventListener("i65:notif-refresh", onRefresh);
    return () => { clearInterval(t); window.removeEventListener("i65:notif-refresh", onRefresh); };
  }, [load]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/read"); } catch (_) {}
      setUnread(0);
    }
  };

  const enable = async () => {
    try {
      await enablePush();
      setPstate("on");
      toast.success("Phone alerts turned on");
    } catch (e) {
      toast.error(e.message || "Could not turn on alerts");
    }
  };

  return (
    <div className="relative">
      <button onClick={toggle} className="relative text-[#d6cec2] hover:text-[#f3c649] transition-colors" data-testid="notif-bell" aria-label="Notifications">
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-[#e0481f] text-white text-[10px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center font-bold" data-testid="notif-unread">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-3 w-80 max-w-[86vw] card-warm rounded-2xl border border-[#3a332c] shadow-2xl z-50 overflow-hidden" data-testid="notif-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#3a332c]">
              <span className="font-display uppercase tracking-wide text-[#f7f3ec] text-sm">Notifications</span>
              <button onClick={() => setOpen(false)} className="text-[#8f857a] hover:text-[#e0481f]"><X size={16} /></button>
            </div>
            {pushSupported() && pstate !== "on" && (
              <button onClick={enable} disabled={pstate === "denied"} className="w-full flex items-center gap-2 px-4 py-3 text-left text-[#f3c649] hover:bg-[#1a1816] border-b border-[#3a332c] text-sm disabled:opacity-60 disabled:cursor-not-allowed" data-testid="notif-enable-push">
                <BellRing size={16} /> {pstate === "denied" ? "Alerts blocked — enable in browser settings" : "Turn on phone alerts"}
              </button>
            )}
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-4 py-6 text-center text-[#8f857a] text-sm">No notifications yet.</p>
              ) : (
                items.map((n) => (
                  <div key={n.id} className="px-4 py-3 border-b border-[#2a251f] last:border-0" data-testid="notif-item">
                    <p className="text-[#f7f3ec] text-sm font-semibold">{n.title}</p>
                    <p className="text-[#a9a196] text-sm mt-0.5">{n.body}</p>
                    <p className="text-[#6b645b] text-[11px] mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
